/**
 * en-GB messages added by Phase 3 task W18 (docs/PHASE-3-PLAN.md). Only that task edits this file.
 * Flat keys, `{name}` placeholders, merged into enGB by ./index.ts.
 */
export const w18EnGB = {
    // ---- Block list (safety check)
    'w18.block.title': 'Safety check',
    'w18.block.description': 'People who must not be signed in. Every sign-in is checked against this list, on every kiosk and link.',
    'w18.block.entries': 'Block list',
    'w18.block.add': 'Add to block list',
    'w18.block.edit': 'Edit block list entry',
    'w18.block.editName': 'Edit {name}',
    'w18.block.name': 'Full name',
    'w18.block.aliases': 'Other names (aliases)',
    'w18.block.aliasesHint': 'One per line. Spelling, capital letters, accents and word order do not matter.',
    'w18.block.company': 'Company',
    'w18.block.criteria': 'Reason',
    'w18.block.criteriaHint': 'Only people who can manage the block list see this. It is never put in an email, a webhook or a kiosk.',
    'w18.block.sites': 'Sites',
    'w18.block.allSites': 'Every site',
    'w18.block.everySite': 'Every site',
    'w18.block.pickSite': 'Choose at least one site, or tick Every site.',
    'w18.block.expires': 'Ends on',
    'w18.block.expiresHint': 'Optional. The entry stops applying after this date.',
    'w18.block.expiresOn': 'Ends {date}',
    'w18.block.updated': 'Last changed',
    'w18.block.alsoKnown': 'Also: {names}',
    'w18.block.status.active': 'In force',
    'w18.block.status.inactive': 'Switched off',
    'w18.block.status.all': 'All',
    'w18.block.activate': 'Switch on',
    'w18.block.deactivate': 'Switch off',
    'w18.block.deleted': 'Entry deleted.',
    'w18.block.test.title': 'Test a name',
    'w18.block.test.help': 'Check whether a name would be caught. Nobody is signed in and nothing is recorded.',
    'w18.block.test.name': 'Name to test',
    'w18.block.test.site': 'Site',
    'w18.block.test.anySite': 'Any site',
    'w18.block.test.button': 'Test',
    'w18.block.test.matched': 'This name would be caught (matching entries: {count}).',
    'w18.block.test.clear': 'No match. This name would not be stopped.',
    'w18.block.matches.title': 'Recent matches',
    'w18.block.matches.when': 'When',
    'w18.block.matches.name': 'Name as typed',
    'w18.block.matches.outcome': 'Outcome',
    'w18.block.matches.source': 'Where',
    'w18.block.outcome.refused': 'Refused',
    'w18.block.outcome.allowed_with_alert': 'Allowed with an alert',
    'w18.block.outcome.offline_warning': 'Warned (kiosk offline)',
    'w18.block.outcome.overridden': 'Overridden',
    'w18.safetyGroup.title': 'Safety check',
    'w18.safetyGroup.description': 'What happens when someone on the block list signs in to this group.',
    'w18.safetyGroup.mode': 'When there is a match',
    'w18.safetyGroup.mode.refuse': 'Refuse the sign-in',
    'w18.safetyGroup.mode.allow_with_alert': 'Allow, and alert reception',
    'w18.safetyGroup.mode.off': 'Do not check this group',
    'w18.safetyGroup.explain.refuse': 'The person is told to see reception and is not signed in. Reception is alerted.',
    'w18.safetyGroup.explain.allow_with_alert': 'The person is signed in and reception is alerted straight away, so someone can go and meet them.',
    'w18.safetyGroup.explain.off': 'Nobody in this group is checked against the block list.',
    'w18.safetySite.title': 'Safety check',
    'w18.safetySite.override': 'Let staff override a refusal',
    'w18.safetySite.overrideHint': 'People who can sign visitors in and out in the portal may sign in someone who was refused. Every override is recorded.',
    // ---- Compliance: document types
    'w18.types.title': 'Compliance documents',
    'w18.types.description': 'The documents you ask contractors to hold, such as insurance or a safety certificate.',
    'w18.types.add': 'Add document type',
    'w18.types.edit': 'Edit document type',
    'w18.types.editName': 'Edit {name}',
    'w18.types.name': 'Name',
    'w18.types.descriptionLabel': 'Description',
    'w18.types.asks': 'What we ask for',
    'w18.types.needsFile': 'A file (PDF or photo)',
    'w18.types.needsIssueDate': 'An issue date',
    'w18.types.needsExpiry': 'An expiry date',
    'w18.types.file': 'File',
    'w18.types.issue': 'Issue date',
    'w18.types.expiry': 'Expiry date',
    'w18.types.reminders': 'Reminders',
    'w18.types.remindersHint': 'Days before expiry, separated by commas, for example 30, 7. Leave empty for none.',
    'w18.types.daysBefore': '{days} days before',
    'w18.types.noReminders': 'None',
    'w18.types.active': 'In use',
    'w18.types.activeBadge': 'In use',
    'w18.types.inactiveBadge': 'Not in use',
    'w18.compGroup.title': 'Compliance',
    'w18.compGroup.description': 'The documents people in this group must hold.',
    'w18.compGroup.noTypes': 'There are no document types yet. Add them under Manage, Compliance.',
    'w18.compGroup.mode.none': 'Not required',
    'w18.compGroup.mode.refuse': 'Required: refuse sign-in if missing',
    'w18.compGroup.mode.warn': 'Required: warn if missing',
    'w18.compGroup.modeHelp': 'Refuse stops the sign-in until the document is on file and in date. Warn lets them in and flags the visit.',
    'w18.compGroup.settings.title': 'Compliance settings for this group',
    'w18.compGroup.selfUpload': 'People may upload their own documents',
    'w18.compGroup.selfUploadHint': 'In the Companion app and on the pre-arrival page. Their uploads wait for review.',
    'w18.compGroup.acceptPending': 'Count documents waiting for review',
    'w18.compGroup.acceptPendingHint': 'If off, a document only counts once someone has accepted it.',
    'w18.compGroup.reminderEmails': 'Extra people to tell about expiring documents',
    'w18.compGroup.reminderEmailsHint': 'Email addresses separated by commas. The person themselves is always told.',
    // ---- Compliance: documents tab
    'w18.doc.standing': 'Standing',
    'w18.doc.outcome.met': 'All required documents are in date',
    'w18.doc.outcome.warn': 'A document is missing or expired (warning)',
    'w18.doc.outcome.refuse': 'A document is missing or expired (sign-in refused)',
    'w18.doc.outcome.not_required': 'No documents are required for this group',
    'w18.doc.noRequirements': 'Nothing is required for this group.',
    'w18.doc.state.valid': 'In date',
    'w18.doc.state.expiring': 'Expiring soon',
    'w18.doc.state.expired': 'Expired',
    'w18.doc.state.missing': 'Missing',
    'w18.doc.state.pending_review': 'Waiting for review',
    'w18.doc.state.rejected': 'Rejected',
    'w18.doc.state.superseded': 'Replaced',
    'w18.doc.induction': 'Induction',
    'w18.doc.induction.valid': 'Passed',
    'w18.doc.induction.expired': 'Expired',
    'w18.doc.induction.missing': 'Not done',
    'w18.doc.induction.not_required': 'Not required',
    'w18.doc.until': 'until {date}',
    'w18.doc.held': 'Documents',
    'w18.doc.none': 'No documents on file.',
    'w18.doc.add': 'Add a document',
    'w18.doc.addButton': 'Add document',
    'w18.doc.added': 'Document added.',
    'w18.doc.type': 'Type of document',
    'w18.doc.file': 'File',
    'w18.doc.fileHint': 'PDF, JPEG or PNG, up to {max} MB.',
    'w18.doc.attached': 'Attached: {name}',
    'w18.doc.reference': 'Reference or certificate number',
    'w18.doc.issueDate': 'Issue date',
    'w18.doc.expiryDate': 'Expiry date',
    'w18.doc.needFile': 'Upload the file first.',
    'w18.doc.needExpiry': 'Enter the expiry date.',
    'w18.doc.needIssue': 'Enter the issue date.',
    'w18.doc.referenceIs': 'Reference: {value}',
    'w18.doc.issuedOn': 'Issued {date}',
    'w18.doc.expiresOn': 'Expires {date}',
    'w18.doc.uploadedBy': 'Added by {who} ({via})',
    'w18.doc.via.portal': 'portal',
    'w18.doc.via.companion': 'app',
    'w18.doc.via.prearrival': 'pre-arrival page',
    'w18.doc.via.import': 'import',
    'w18.doc.reviewedBy': 'Reviewed by {who}, {when}',
    'w18.doc.rejectedBecause': 'Rejected: {reason}',
    'w18.doc.view': 'View file',
    'w18.doc.accept': 'Accept',
    'w18.doc.acceptName': 'Accept {name}',
    'w18.doc.reject': 'Reject',
    'w18.doc.rejectName': 'Reject {name}',
    'w18.doc.rejectReason': 'Reason for rejecting',
    'w18.doc.history': 'Replaced documents',
    // ---- Compliance report
    'w18.report.title': 'Compliance report',
    'w18.report.description': 'Who is missing a document, or about to lose one.',
    'w18.report.group': 'Group',
    'w18.report.allGroups': 'All groups',
    'w18.report.show': 'Show',
    'w18.report.show.problems': 'Problems (missing, expired, rejected)',
    'w18.report.show.expiring': 'Expiring soon',
    'w18.report.show.all': 'Everything',
    'w18.report.within': 'Expiring within (days)',
    'w18.report.person': 'Person',
    'w18.report.groupCol': 'Group',
    'w18.report.document': 'Document',
    'w18.report.expiry': 'Expiry',
    'w18.report.daysAgo': '{days} days ago',
    'w18.report.inDays': 'in {days} days',
    // ---- Inductions
    'w18.ind.title': 'Inductions',
    'w18.ind.description': 'A video or document and a short quiz that people pass before they can sign in.',
    'w18.ind.add': 'Add induction',
    'w18.ind.edit': 'Edit induction',
    'w18.ind.editName': 'Edit {name}',
    'w18.ind.name': 'Name',
    'w18.ind.body': 'Text',
    'w18.ind.bodyHint': 'Shown with the video or document, or on its own.',
    'w18.ind.media': 'Video or document',
    'w18.ind.uploadMedia': 'Upload a video (MP4) or PDF',
    'w18.ind.replaceMedia': 'Replace the video or PDF',
    'w18.ind.mediaHint': 'MP4 (H.264) up to {max} MB, or a PDF.',
    'w18.ind.removeMedia': 'Remove it',
    'w18.ind.mediaWillBeRemoved': 'The current file will be removed when you save.',
    'w18.ind.undo': 'Undo',
    'w18.ind.newMedia': 'Uploaded: {name}. It is used when you save.',
    'w18.ind.videoTitle': 'Induction video: {name}',
    'w18.ind.openPdf': 'Open the PDF',
    'w18.ind.minWatch': 'Minimum watch time (seconds)',
    'w18.ind.minWatchHint': 'The quiz opens after this much of the video has played. 0 means no minimum.',
    'w18.ind.quiz': 'Quiz',
    'w18.ind.passMark': 'Pass mark (%)',
    'w18.ind.validity': 'Valid for (months)',
    'w18.ind.validityHint': 'After this a pass runs out and the person does the induction again.',
    'w18.ind.assign': 'Who must do it',
    'w18.ind.assignHint': 'People in these groups, at these sites, must hold a valid pass to sign in.',
    'w18.ind.group': 'Group',
    'w18.ind.chooseGroup': 'Choose a group',
    'w18.ind.site': 'Site',
    'w18.ind.everySite': 'Every site',
    'w18.ind.addAssign': 'Add a group',
    'w18.ind.remove': 'Remove',
    'w18.ind.removeAssign': 'Remove row {n}',
    'w18.ind.active': 'In use',
    'w18.ind.versionNote': 'This is version {version}. Saving changes to the video or quiz makes a new version.',
    'w18.ind.needName': 'Give the induction a name.',
    'w18.ind.fixQuiz': 'Fix the problems in the quiz first.',
    'w18.ind.needGroup': 'Choose a group for every row.',
    'w18.ind.noAssign': 'Nobody yet',
    'w18.ind.noMedia': 'no video',
    'w18.ind.mediaKind.video': 'video',
    'w18.ind.mediaKind.pdf': 'PDF',
    'w18.ind.listMeta': 'Version {version}, valid {months} months, {media}',
    'w18.ind.quizMeta': '{count} questions, pass at {mark}%',
    'w18.ind.passes': 'Passes',
    'w18.ind.passesName': 'Passes for {name}',
    'w18.ind.passesFor': 'Passes: {name}',
    'w18.ind.pass.who': 'Person',
    'w18.ind.pass.passed': 'Passed',
    'w18.ind.pass.expires': 'Runs out',
    'w18.ind.pass.score': 'Score',
    'w18.ind.pass.via': 'Done on',
    'w18.ind.pass.version': 'version {version}',
    'w18.ind.via.kiosk': 'Kiosk',
    'w18.ind.via.companion': 'App',
    'w18.ind.via.prearrival': 'Pre-arrival page',
    'w18.ind.via.contactless': 'Contactless sign-in',
    'w18.ind.via.portal': 'Portal',
    'w18.ind.helpTitle': 'How inductions work',
    'w18.ind.help': 'People watch the video, then answer the quiz. A kiosk marks the quiz offline and the server marks it again when the visit arrives; the server result counts.',
    // ---- NFC cards
    'w18.nfc.title': 'NFC cards',
    'w18.nfc.description': 'Cards and key fobs that people tap on a kiosk to sign in and out.',
    'w18.nfc.person': 'Person',
    'w18.nfc.label': 'Name for the card',
    'w18.nfc.card': 'Card',
    'w18.nfc.unnamed': 'Card',
    'w18.nfc.lastUsedCol': 'Last used',
    'w18.nfc.lastUsed': 'Last used {date}',
    'w18.nfc.neverUsed': 'Never used',
    'w18.nfc.registeredVia': 'Registered by {via}, {date}',
    'w18.nfc.via.portal': 'portal',
    'w18.nfc.via.import': 'import',
    'w18.nfc.via.kiosk': 'kiosk',
    'w18.nfc.status.all': 'All',
    'w18.nfc.status.active': 'Active',
    'w18.nfc.status.blocked': 'Blocked',
    'w18.nfc.status.lost': 'Lost',
    'w18.nfc.status.retired': 'Retired',
    'w18.nfc.reason.lost': 'lost',
    'w18.nfc.reason.stolen': 'stolen',
    'w18.nfc.reason.other': 'other',
    'w18.nfc.blockReason': 'Reason',
    'w18.nfc.block': 'Block',
    'w18.nfc.blockName': 'Block {name}',
    'w18.nfc.unblock': 'Unblock',
    'w18.nfc.unblockName': 'Unblock {name}',
    'w18.nfc.retire': 'Retire',
    'w18.nfc.stolenNote': 'A stolen card cannot be unblocked.',
    'w18.nfc.addTitle': 'Add a card',
    'w18.nfc.addHelp': 'Type the card number, or click in the box and tap the card on a USB reader.',
    'w18.nfc.uid': 'Card number',
    'w18.nfc.uidPlaceholder': '04:A2:2B:1C',
    'w18.nfc.badUid': 'That is not a card number. Use letters A to F and numbers, for example 04:A2:2B:1C.',
    'w18.nfc.addButton': 'Add card',
    'w18.nfc.added': 'Card added.',
    'w18.nfc.noneForMember': 'This person has no cards.',
    'w18.nfc.close': 'Close',
    'w18.nfc.import.open': 'Import from CSV',
    'w18.nfc.import.title': 'Import cards',
    'w18.nfc.import.help': 'One card per row: the person’s email address (or member id), the card number and a name for the card. A first row of headings is skipped.',
    'w18.nfc.import.file': 'CSV file',
    'w18.nfc.import.chosen': 'Using {name}',
    'w18.nfc.import.paste': 'Or paste the rows here',
    'w18.nfc.import.unreadable': 'That file could not be read.',
    'w18.nfc.import.button': 'Import',
    'w18.nfc.import.result': '{added} of {total} rows added, {skipped} skipped.',
    'w18.nfc.import.skipped': 'Skipped rows',
    'w18.nfc.import.row': 'Row',
    'w18.nfc.import.reason': 'Why',
    'w18.nfc.enrol.open': 'Enrol cards on a kiosk',
    'w18.nfc.enrol.title': 'Enrol cards on a kiosk',
    'w18.nfc.enrol.help': 'Choose a kiosk and the people who need a card. The kiosk asks for each person in turn and registers the card they tap. This stays open for 15 minutes.',
    'w18.nfc.enrol.kiosk': 'Kiosk',
    'w18.nfc.enrol.chooseKiosk': 'Choose a kiosk',
    'w18.nfc.enrol.offline': 'offline',
    'w18.nfc.enrol.group': 'Group',
    'w18.nfc.enrol.chooseGroup': 'Choose a group',
    'w18.nfc.enrol.people': 'People',
    'w18.nfc.enrol.chosen': '{count} chosen (up to 200).',
    'w18.nfc.enrol.start': 'Start enrolling',
    'w18.nfc.enrol.waiting': 'Waiting for cards on the kiosk. Time left: {time}.',
    'w18.nfc.enrol.ended.done': 'Every card has been registered.',
    'w18.nfc.enrol.ended.cancelled': 'Enrolment was cancelled.',
    'w18.nfc.enrol.ended.expired': 'Enrolment ran out of time. Start it again for anyone still waiting.',
    'w18.nfc.enrol.ended.open': 'Enrolment is open.',
    'w18.nfc.enrol.progress': '{done} of {total} cards registered',
    'w18.nfc.enrol.registered': 'Registered',
    'w18.nfc.enrol.waitingItem': 'Waiting',
    'w18.nfc.enrol.cancel': 'Cancel enrolment',
    'w18.nfc.enrol.cancelled': 'Enrolment cancelled.',
    'w18.nfcGroup.title': 'NFC cards',
    'w18.nfcGroup.description': 'Tap-to-sign-in cards for this group.',
    'w18.nfcGroup.enabled': 'People in this group can sign in with a card',
    'w18.nfcGroup.requirePin': 'Ask for their PIN after the tap',
    'w18.nfcGroup.requirePinHint': 'A lost card then cannot be used on its own.',
    'w18.nfcGroup.max': 'Most cards per person',
};
//# sourceMappingURL=w18.js.map